import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Grid from '@mui/material/Grid2';
import { Alert } from '@mui/material';
import { DriveEta, DirectionsRun, CheckCircle, Cancel, NotificationsActive } from '@mui/icons-material';
import { BoxCenterStart } from '../style/box';
import { commonStyles } from "../style/common";
import { fetchData, postData } from "../func/api";
import Snackbar from '@mui/material/Snackbar';
import { BaseDialog } from "../components/base_dialog";
import { Complete } from "../components/complete";
import { CalloutSection } from "../components/callout_section";

import { InputDialog } from "../components/input_dialog";
import { useAuth } from "../contexts/AuthContext";
import { ROLE_UUID } from "../const/config";
import SkeletonLoading from "../components/skeleton_loading";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

//  業務可否コメント入力用バリデーションスキーマ
const validationSchema = yup.object().shape({
    note: yup
        .string()
        .required("コメントは必須です。") // 必須化
        .max(1000, "最大1000文字です"),
});

const CalloutRegistration = () => {
    const [earthquakeData, setEarthquakeData] = useState(null);
    const [isAssigned, setIsAssigned] = useState(false);
    const [assignWorkData, setAssignWorkData] = useState(null);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertSeverity, setAlertSeverity] = useState("success");
    const [hideCalloutSection, setHideCalloutSection] = useState(false);
    const [selectedDriveValue, setSelectedDriveValue] = useState(null);
    const [isConfirmDisabled, setIsConfirmDisabled] = useState(false);
    const [loading, setLoading] = useState(true);
    
    // ダイアログ管理用State
    const [baseDialogType, setBaseDialogType] = useState(null); // "drivable" でのみ BaseDialog を使用
    const [isCommentDialogOpen, setIsCommentDialogOpen] = useState(false); // コメント入力ダイアログ

    // コメント入力関連のState
    const [pendingWorkableValue, setPendingWorkableValue] = useState(null); // "possible" or "impossible"
    const [workableComment, setWorkableComment] = useState(""); // 業務対応可能時に一時保持するコメント

    const { uuid } = useParams();
    const { currentUser } = useAuth();

    //  react-hook-form の初期化
    const {
        handleSubmit,
        register,
        formState: { errors },
        reset,
        trigger
    } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onBlur',
        defaultValues: {
            note: '', // コメントフィールド名
        },
    });

    // 追加: ロールがVIEWERならエラー表示
    useEffect(() => {
        if (currentUser?.role_id === ROLE_UUID.VIEWER) {
            setAlertMessage("閲覧権限のユーザーはこの画面を利用できません。");
            setAlertSeverity("error");
            setAlertOpen(true);
        }
    }, [currentUser]);

    useEffect(() => {
        // report_workテーブルのデータ取得 (既存のロジック)
        const getReportWork = async () => {
            try {
                let reportWorkData = null;
                const earthquake = await fetchData(`api/get_earthquake/${uuid}`);
                const calloutData = await fetchData(`api/get_callout/${earthquake.report_uuid}`);
                // ログインユーザーの業務割当結果を抽出
                let loginUserCalloutData = null;
                if (calloutData.length > 0) {
                    loginUserCalloutData = calloutData.find(item => item.user_uuid === currentUser.id);
                }
                if (loginUserCalloutData && loginUserCalloutData.report_uuid && loginUserCalloutData.user_uuid) {
                    reportWorkData = await fetchData(`api/get_report_work/${loginUserCalloutData.report_uuid}/${loginUserCalloutData.user_uuid}`);
                    setAssignWorkData(reportWorkData);
                }
                setIsAssigned(reportWorkData && Object.keys(reportWorkData).length > 0);
                setEarthquakeData(earthquake);
            } catch (error) {
                setIsAssigned(false);
            } finally {
                setLoading(false)
            }
        };
        if (uuid && currentUser?.role_id !== ROLE_UUID.VIEWER) {
            getReportWork();
        }
    }, [uuid]);

    // 業務対応可否ボタンクリック時の処理 (InputDialog を表示)
    const openWorkableDialog = (value) => {
        setPendingWorkableValue(value);
        reset({ note: '' }); // フォームの値をリセット
        setIsCommentDialogOpen(true); // コメント入力ダイアログを表示
    };

    // 運転可否ボタンクリック時の処理 (BaseDialog を表示)
    const openDrivableDialog = (value) => {
        setSelectedDriveValue(value);
        setBaseDialogType("drivable"); // BaseDialog を使用
    };

    //  InputDialog の「OK」クリック時の処理
    const handleSendApproval = async (data) => {
        // data.note にはバリデーション済みのコメントが入っている
        const comment = data.note;
        const selectedWorkableValue = pendingWorkableValue;

        setIsCommentDialogOpen(false);

        try {
            if (selectedWorkableValue === "impossible") {
                //  業務対応不可の場合: POSTして完了画面へ遷移
                const postDataPayload = {
                    report_uuid: earthquakeData.report_uuid,
                    user_uuid: currentUser.id,
                    is_workable: 0, // 対応不可として登録
                    is_drivable: 0,  // 運転不可として登録
                    comment: comment,
                };

                await postData("api/post_callout", postDataPayload);
                window.location.href = `/callout_complete/${uuid}`;
            } else {
                //  業務対応可能の場合: POSTせず、コメントを一時保持し、運転可否の確認へ遷移
                setWorkableComment(comment);

                // 画面を運転可否の確認へ切り替え
                setHideCalloutSection(true);
            }
        } catch (err) {
            setAlertOpen(true);
            setAlertMessage("登録失敗しました。再度時間をおいてからお試しください。");
            setAlertSeverity("error");
        }

        // フォームをリセット (ダイアログが閉じられた後に実行)
        reset({ note: '' });
    };

    // InputDialog の「キャンセル」クリック時の処理
    const handleCommentDialogClose = () => {
        setIsCommentDialogOpen(false);
        reset({ note: '' }); // キャンセル時もフォームをリセット
    };


    // BaseDialog（運転可否）の「OK」クリック時の処理
    const handleBaseDialogConfirm = () => {
        if (baseDialogType === "drivable") {
            handleSendDriveApproval();
        }
        setBaseDialogType(null);
    };

    // BaseDialog（運転可否）の「キャンセル」クリック時の処理
    const handleBaseDialogCancel = () => {
        setBaseDialogType(null);
    };

    // 運転可否の送信処理 (対応可能 + 運転可否 の最終的なPOST)
    const handleSendDriveApproval = async () => {
        if (!selectedDriveValue || !workableComment) return;

        try {
            //  業務対応可否（is_workable=1）、運転可否（is_drivable）、一時保持したコメント（workableComment）をまとめてPOST
            const data = {
                report_uuid: earthquakeData.report_uuid,
                user_uuid: currentUser.id,
                is_workable: 1, // 対応可能として登録
                is_drivable: selectedDriveValue === "possible" ? 1 : 0, // 運転可否
                comment: workableComment, // 業務対応可能時に入力したコメント
            };

            await postData("api/post_callout", data);

            window.location.href = `/callout_complete/${uuid}`;
        } catch (err) {
            setAlertOpen(true);
            setAlertMessage("登録失敗しました。再度時間をおいてからお試しください。");
            setAlertSeverity("error");
        }
    };

    const handleConfirmClick = async () => {
        setIsConfirmDisabled(true);
        try {
            const data = {
                uuid: uuid,
                is_answered: 1, // 回答済として登録
            };

            await postData("api/post_callout_answered", data);

            setAlertMessage("確認が完了しました");
            setAlertSeverity("success");
            setAlertOpen(true);
        } catch (err) {
            setAlertOpen(true);
            setAlertMessage("登録失敗しました。再度時間をおいてからお試しください。");
            setAlertSeverity("error");
        }
    };

    if (currentUser?.role_id === ROLE_UUID.VIEWER) {
        return (
            <BoxCenterStart sx={commonStyles.page}>
                <Alert severity="error">
                    閲覧権限のユーザーはこの画面を利用できません。
                </Alert>
            </BoxCenterStart>
        );
    }

    if (loading) {
        return <SkeletonLoading />;
    }

    return (
        <BoxCenterStart sx={commonStyles.page}>
            <Grid
                container
                spacing={{ xs: 3, sm: 5, md: 7 }}
                columns={12}
                sx={commonStyles.container}
            >
                <>
                    {isAssigned ? (
                        // 業務割当済みの場合
                        <Complete
                            iconComponent={NotificationsActive}
                            color={'#0A7CB7'}
                            mainMessage={"業務が割り当てられました"}
                            workData={assignWorkData}
                            subMessage={"ご自身の業務内容を確認しましたら、「確認ボタン」をクリックしてください。\nその後、ブラウザを閉じてください。"}
                            onClick={handleConfirmClick}
                            disabled={isConfirmDisabled}
                            buttonName={"確認"}
                        />
                    ) : (
                        // 業務未割当の場合のフロー
                        <>
                            {/* 1. 業務対応可否セクション */}
                            {!hideCalloutSection ? (
                                <CalloutSection
                                    titleIcon={DirectionsRun}
                                    title={"地震対応可否"}
                                    message={"地震対応の招集が届いています。\nボタンをクリックして出社可否を回答してください。"}
                                    availableIconComponent={<CheckCircle />}
                                    cancelIconComponent={<Cancel />}
                                    onClickAvailable={() => openWorkableDialog("possible")}
                                    onClickUnAvailable={() => openWorkableDialog("impossible")}
                                    possibleButtonName={"出社可能"}
                                    impossibleButtonName={"出社不可"}
                                />
                            ) : (
                                // 2. 運転可否セクション (対応可能回答後のみ表示)
                                <>
                                    <CalloutSection
                                        titleIcon={DriveEta}
                                        title={"運転可否"}
                                        message={"ボタンをクリックして、車の運転ができそうかを回答してください。"}
                                        availableIconComponent={<CheckCircle />}
                                        cancelIconComponent={<Cancel />}
                                        onClickAvailable={() => openDrivableDialog("possible")}
                                        onClickUnAvailable={() => openDrivableDialog("impossible")}
                                        possibleButtonName={"運転可能"}
                                        impossibleButtonName={"運転不可"}
                                    />
                                </>
                            )}
                        </>
                    )}
                </>
            </Grid>

            {/*  業務対応可否回答時のコメント必須入力ダイアログ (InputDialogを使用) */}
            <InputDialog
                open={isCommentDialogOpen}
                onClose={handleCommentDialogClose}
                title={pendingWorkableValue === "possible" ? "出社可能" : "出社不可"}
                message={
                    pendingWorkableValue === "possible"
                        ? "出社可能として登録します。\n今いる場所と、出社可能時刻を入力してください。"
                        : "出社不可として登録します。\n今いる場所と、出社可能時刻を入力してください。"
                }
                onConfirm={handleSubmit(handleSendApproval)} //  handleSubmit経由でバリデーションと処理を実行
                confirmText='OK'
                cancelText='キャンセル'
                errors={errors}
                label="今いる場所、出社可能時刻"
                registerProps={
                    {
                        ...register("note", {
                            onBlur: () => trigger('note') // マウスが外れた時にもバリデーションを実行
                        })
                    }}
            />

            {/* 運転可否回答時のBaseDialog */}
            <BaseDialog
                open={!!baseDialogType}
                onClose={handleBaseDialogCancel}
                title={"運転可否登録"}
                message={
                    selectedDriveValue === "possible"
                        ? "運転可能として登録を行います。よろしいですか？"
                        : "運転不可として登録を行います。よろしいですか？"
                }
                onConfirm={handleBaseDialogConfirm}
                confirmText="OK"
                cancelText="キャンセル"
            />

            {/* 失敗時のSnackbar */}
            {alertMessage && (
                <Snackbar
                    open={alertOpen}
                    autoHideDuration={3000}
                    onClose={() => setAlertOpen(false)}
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                >
                    <Alert severity={alertSeverity} onClose={() => setAlertOpen(false)}>
                        {alertMessage}
                    </Alert>
                </Snackbar>
            )}
        </BoxCenterStart>
    );
};

export default CalloutRegistration;